/* ==========================================================================
   help-hub.js
   Behaviour for the Little Stackers support hub (/pages/faqs).

   Four independent features, each a no-op if its markup isn't on the page:
     1. Search      — indexes the FAQ DOM, drives the hero dropdown + live
                      filtering of the accordion list below.
     2. FAQ         — deep links (#anchor opens + highlights), sticky category
                      nav with scroll-spy.
     3. Video       — thumbnail facades that open a <dialog> lightbox. No
                      YouTube script runs until someone actually clicks play.
     4. Contact     — inline validation, character counter, and topic-driven
                      deflection that surfaces relevant FAQs before sending.

   No dependencies. Safe to load with `defer`.
   ========================================================================== */

(function () {
  'use strict';

  // Every hub section emits its own <script src> so each one works standalone
  // (and in the theme editor when dropped onto another page). That means this
  // file can execute up to four times on the hub — bail on all but the first,
  // otherwise every listener gets bound once per section.
  if (window.__helpHubLoaded) return;
  window.__helpHubLoaded = true;

  var SCROLL_OFFSET = 96;

  // Set as soon as the visitor scrolls under their own steam. Anything we
  // scroll on page load checks this first: yanking the page out from under
  // someone who has already started reading is worse than not jumping at all.
  // Only real input arms it, so our own programmatic scrolls can't trip it.
  var userScrolled = false;
  ['wheel', 'touchstart', 'keydown'].forEach(function (evt) {
    window.addEventListener(
      evt,
      function () {
        userScrolled = true;
      },
      { passive: true, once: true }
    );
  });

  // Listeners on document/window would otherwise stack up (and close over
  // detached elements) each time the theme editor re-renders a section, so
  // they're all registered against a signal that init() aborts on re-entry.
  var listeners = null;

  /* ---------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------- */

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  // Fold case, strip diacritics, straighten smart quotes, drop punctuation.
  // Lets "dont" match "don’t" and "sterilise" match "Sterilise".
  function normalize(str) {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[‘’ʼ]/g, "'")
      .replace(/[^a-z0-9' ]+/g, ' ')
      .replace(/'/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments,
        self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  // `instant` skips the animation outright. Callers arriving with the page
  // (a deep link, a form post round-trip) must pass it — see landOn below.
  function scrollToEl(el, instant) {
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (instant || reduce) {
      window.scrollTo({ top: top, behavior: 'instant' });
      return;
    }

    // A smooth scroll needs animation frames, and a page opened in a
    // background tab gets none — the scroll silently never starts, stranding
    // whoever arrived on a #deep-link. Ask for smooth, then confirm we
    // actually moved and snap if we didn't: landing on the answer matters
    // more than the animation. No-ops when smooth is working normally.
    var from = window.pageYOffset;
    window.scrollTo({ top: top, behavior: 'smooth' });
    setTimeout(function () {
      if (Math.abs(window.pageYOffset - from) < 8 && Math.abs(top - from) >= 8) {
        window.scrollTo({ top: top, behavior: 'instant' });
      }
    }, 250);
  }

  // Put `el` at the top of the screen once the page has finished loading.
  //
  // Instant, never smooth, and never before `load`. The theme sets
  // `scroll-behavior: smooth` on <html>, which makes Chrome ANIMATE even the
  // jump it does for a fragment in the URL — and that animation is routinely
  // abandoned while the page is still loading, so you end up at the very top
  // with the right anchor sitting in the address bar and no clue why nothing
  // moved. `behavior: 'instant'` is the one thing CSS smooth doesn't override.
  function landOn(el) {
    var go = function () {
      if (!userScrolled) scrollToEl(el, true);
    };
    if (document.readyState === 'complete') {
      go();
    } else {
      window.addEventListener('load', go, { once: true });
    }
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Wrap each matched term in <mark>. Operates on plain text only, and escapes
  // before injecting, so answer HTML is never re-parsed from user input.
  function highlight(text, terms) {
    if (!terms.length) return escapeHtml(text);
    var safe = escapeHtml(text);
    terms
      .slice()
      .sort(function (a, b) {
        return b.length - a.length;
      })
      .forEach(function (term) {
        if (term.length < 2) return;
        var re = new RegExp('(' + term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
        // Skip anything already inside a tag or an existing <mark>.
        safe = safe.replace(/(<[^>]*>)|([^<]+)/g, function (m, tag, txt) {
          if (tag) return tag;
          return txt.replace(re, '<mark>$1</mark>');
        });
      });
    return safe;
  }

  /* ---------------------------------------------------------------------
     FAQ index — the single source of truth shared by search and deflection
     --------------------------------------------------------------------- */

  var faqRoot = $('[data-hh-faq]');
  var index = [];

  function buildIndex() {
    if (!faqRoot) return;
    index = $$('[data-hh-faq-item]', faqRoot).map(function (item) {
      var qEl = $('[data-hh-faq-q-text]', item);
      var aEl = $('[data-hh-faq-a]', item);
      var group = item.closest('[data-hh-faq-group]');
      var question = (qEl ? qEl.textContent : '').trim();
      var answer = (aEl ? aEl.textContent : '').replace(/\s+/g, ' ').trim();
      return {
        el: item,
        group: group,
        id: item.id,
        category: group ? group.getAttribute('data-hh-faq-group') : '',
        categoryLabel: group ? group.getAttribute('data-hh-faq-label') || '' : '',
        question: question,
        qNorm: normalize(question),
        aNorm: normalize(answer),
        // Merchant-supplied extra search terms (schema field on each block).
        kNorm: normalize(item.getAttribute('data-hh-keywords') || '')
      };
    });

    // Keep the original question text so highlighting can be undone cleanly.
    index.forEach(function (entry) {
      var qEl = $('[data-hh-faq-q-text]', entry.el);
      if (qEl && !qEl.hasAttribute('data-hh-original')) {
        qEl.setAttribute('data-hh-original', entry.question);
      }
    });
  }

  function search(query) {
    var terms = normalize(query).split(' ').filter(Boolean);
    // Drop single letters — "O-ring" normalises to "o ring", and a bare "o"
    // appears in almost every answer, which would match the whole list.
    var meaty = terms.filter(function (t) {
      return t.length > 1;
    });
    if (meaty.length) terms = meaty;
    if (!terms.length) return null;

    return index
      .map(function (entry) {
        var score = 0;
        for (var i = 0; i < terms.length; i++) {
          var t = terms[i];
          if (entry.qNorm.indexOf(t) !== -1) {
            // Word-boundary hits in the question rank highest.
            score += new RegExp('\\b' + t).test(entry.qNorm) ? 10 : 6;
          } else if (entry.kNorm.indexOf(t) !== -1) {
            score += 5;
          } else if (entry.aNorm.indexOf(t) !== -1) {
            score += 2;
          } else {
            return null; // every term must appear somewhere
          }
        }
        return { entry: entry, score: score, terms: terms };
      })
      .filter(Boolean)
      .sort(function (a, b) {
        return b.score - a.score;
      });
  }

  /* ---------------------------------------------------------------------
     1. Search UI
     --------------------------------------------------------------------- */

  function initSearch() {
    var root = $('[data-hh-search]');
    if (!root || !faqRoot) return;

    var input = $('[data-hh-search-input]', root);
    var clearBtn = $('[data-hh-search-clear]', root);
    var list = $('[data-hh-search-results]', root);
    var status = $('[data-hh-faq-status-text]', faqRoot);
    var resetBtn = $('[data-hh-faq-reset]', faqRoot);
    var activeIdx = -1;

    function closeList() {
      list.hidden = true;
      activeIdx = -1;
      input.setAttribute('aria-expanded', 'false');
    }

    function renderSuggestions(results, terms) {
      if (!results) {
        closeList();
        return;
      }
      if (!results.length) {
        list.innerHTML = '<li class="hh-search__empty">No matches. Try a different word, or scroll down to the contact form.</li>';
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        return;
      }
      list.innerHTML = results
        .slice(0, 6)
        .map(function (r, i) {
          return (
            '<li class="hh-search__result" role="option" id="hh-sr-' + i + '" aria-selected="false">' +
            '<a href="#' + r.entry.id + '">' +
            '<span class="hh-search__result-q">' + highlight(r.entry.question, terms) + '</span>' +
            '<span class="hh-search__result-cat">' + escapeHtml(r.entry.categoryLabel) + '</span>' +
            '</a></li>'
          );
        })
        .join('');
      list.hidden = false;
      activeIdx = -1;
      input.setAttribute('aria-expanded', 'true');
    }

    // Filter the accordion list itself, so the page below mirrors the query.
    function applyFilter(results, terms) {
      var visible = 0;

      index.forEach(function (entry) {
        var match = !results || results.some(function (r) {
          return r.entry === entry;
        });
        entry.el.hidden = !match;
        if (match) visible++;

        var qEl = $('[data-hh-faq-q-text]', entry.el);
        if (qEl) {
          var original = qEl.getAttribute('data-hh-original') || entry.question;
          qEl.innerHTML = results ? highlight(original, terms) : escapeHtml(original);
        }
        // Auto-expand single strong matches so the answer is right there.
        if (results && results.length <= 3 && match) entry.el.open = true;
        if (!results) entry.el.open = false;
      });

      $$('[data-hh-faq-group]', faqRoot).forEach(function (group) {
        var any = $$('[data-hh-faq-item]', group).some(function (i) {
          return !i.hidden;
        });
        group.hidden = !any;
      });

      faqRoot.classList.toggle('is-filtering', !!results);
      faqRoot.classList.toggle('is-empty', !!results && visible === 0);

      if (status && results) {
        status.textContent =
          visible === 0
            ? 'No answers match “' + input.value.trim() + '”'
            : visible + (visible === 1 ? ' answer' : ' answers') + ' matching “' + input.value.trim() + '”';
      }

      // Category counts are meaningless while filtered.
      $$('[data-hh-faq-nav-link]', document).forEach(function (link) {
        var handle = link.getAttribute('data-hh-faq-nav-link');
        var group = $('[data-hh-faq-group="' + handle + '"]', faqRoot);
        link.parentElement.hidden = !!results && (!group || group.hidden);
      });
    }

    var run = debounce(function () {
      var q = input.value.trim();
      root.classList.toggle('is-filled', q.length > 0);
      if (q.length < 2) {
        closeList();
        applyFilter(null, []);
        return;
      }
      var results = search(q);
      var terms = normalize(q).split(' ').filter(Boolean);
      renderSuggestions(results, terms);
      applyFilter(results, terms);
    }, 140);

    input.addEventListener('input', run);

    input.addEventListener('keydown', function (e) {
      var items = $$('.hh-search__result', list);
      if (e.key === 'Escape') {
        closeList();
        input.blur();
        return;
      }
      if (e.key === 'Enter') {
        var target = items[activeIdx >= 0 ? activeIdx : 0];
        if (target && !list.hidden) {
          e.preventDefault();
          target.querySelector('a').click();
        }
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (!items.length || list.hidden) return;
      e.preventDefault();
      activeIdx = e.key === 'ArrowDown'
        ? (activeIdx + 1) % items.length
        : (activeIdx <= 0 ? items.length : activeIdx) - 1;
      items.forEach(function (li, i) {
        li.classList.toggle('is-active', i === activeIdx);
        li.setAttribute('aria-selected', i === activeIdx ? 'true' : 'false');
      });
      input.setAttribute('aria-activedescendant', items[activeIdx].id);
      items[activeIdx].scrollIntoView({ block: 'nearest' });
    });

    list.addEventListener('click', function (e) {
      if (e.target.closest('a')) closeList();
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) closeList();
    }, { signal: listeners.signal });

    function reset() {
      input.value = '';
      root.classList.remove('is-filled');
      closeList();
      applyFilter(null, []);
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        reset();
        input.focus();
      });
    }
    if (resetBtn) resetBtn.addEventListener('click', reset);

    // "/" focuses search, the way docs sites do — but never while typing.
    document.addEventListener('keydown', function (e) {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      var tag = (document.activeElement && document.activeElement.tagName) || '';
      if (/INPUT|TEXTAREA|SELECT/.test(tag) || document.activeElement.isContentEditable) return;
      e.preventDefault();
      input.focus();
      input.select();
    }, { signal: listeners.signal });
  }

  /* ---------------------------------------------------------------------
     2. FAQ — deep links + scroll-spy
     --------------------------------------------------------------------- */

  function openFromHash(hash, smooth) {
    if (!faqRoot || !hash) return false;
    var id = hash.replace(/^#/, '');
    if (!id) return false;

    // A category anchor scrolls to the group heading.
    var group = $('[data-hh-faq-group="' + CSS.escape(id) + '"]', faqRoot);
    if (group) {
      scrollToEl(group);
      return true;
    }

    var item = document.getElementById(id);
    if (!item || !item.hasAttribute('data-hh-faq-item')) return false;

    item.open = true;
    item.classList.add('is-target');
    setTimeout(function () {
      item.classList.remove('is-target');
    }, 2400);
    if (smooth !== false) scrollToEl(item);
    return true;
  }

  function initFaq() {
    if (!faqRoot) return;

    // Deep link on load. rAF so layout has settled before we measure.
    if (location.hash) {
      requestAnimationFrame(function () {
        openFromHash(location.hash);
      });
    }

    // Intercept in-page links to FAQ anchors from anywhere on the page.
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="#"]');
      if (!link) return;
      var url = new URL(link.href, location.href);
      if (url.pathname !== location.pathname || !url.hash) return;
      if (openFromHash(url.hash)) {
        e.preventDefault();
        history.replaceState(null, '', url.hash);
      }
    }, { signal: listeners.signal });

    window.addEventListener('hashchange', function () {
      openFromHash(location.hash);
    }, { signal: listeners.signal });

    // Keep the URL in sync when an item is opened by hand, so it's shareable.
    $$('[data-hh-faq-item]', faqRoot).forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (item.open && item.id) history.replaceState(null, '', '#' + item.id);
      });
    });

    // Scroll-spy the category nav.
    var links = $$('[data-hh-faq-nav-link]');
    var groups = $$('[data-hh-faq-group]', faqRoot);
    if (!links.length || !groups.length || !('IntersectionObserver' in window)) return;

    var visible = new Set();
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var handle = entry.target.getAttribute('data-hh-faq-group');
          if (entry.isIntersecting) visible.add(handle);
          else visible.delete(handle);
        });
        // Highlight the topmost group currently in view.
        var current = null;
        for (var i = 0; i < groups.length; i++) {
          var h = groups[i].getAttribute('data-hh-faq-group');
          if (visible.has(h)) {
            current = h;
            break;
          }
        }
        links.forEach(function (link) {
          link.classList.toggle('is-current', link.getAttribute('data-hh-faq-nav-link') === current);
        });
      },
      { rootMargin: '-100px 0px -55% 0px' }
    );
    groups.forEach(function (g) {
      observer.observe(g);
    });
  }

  /* ---------------------------------------------------------------------
     3. Video lightbox
     --------------------------------------------------------------------- */

  // YouTube serves a 120x90 grey placeholder (HTTP 200, so onerror is no help)
  // when a video has no original-aspect still. Detect it by size and fall back.
  function initThumbFallbacks() {
    $$('img[data-hh-thumb-fallback]').forEach(function (img) {
      var fallback = img.getAttribute('data-hh-thumb-fallback');
      if (!fallback) return;

      function swap() {
        // The src check stops a placeholder-sized fallback looping forever.
        if (img.naturalWidth && img.naturalWidth <= 160 && img.src !== fallback) {
          img.src = fallback;
        }
      }

      if (img.complete) swap();
      img.addEventListener('load', swap);
    });
  }

  // The rail is a horizontal scroller at every width. Touch swipes it; pointer
  // users get the arrow pair, because the scrollbar is hidden and the overflow
  // is otherwise invisible. CSS decides whether the arrows are allowed to show
  // at this width — all we do here is say whether there's anything to scroll.
  function initVideoRail() {
    $$('[data-hh-rail]').forEach(function (rail) {
      var scope = rail.closest('.hh-videos') || document;
      var nav = $('[data-hh-rail-nav]', scope);
      if (!nav) return;

      var prev = $('[data-hh-rail-prev]', nav);
      var next = $('[data-hh-rail-next]', nav);

      // A screenful minus one card, so a familiar card stays on screen to
      // anchor the eye instead of the whole rail swapping out.
      function step() {
        var card = rail.firstElementChild;
        var w = card ? card.getBoundingClientRect().width : 240;
        return Math.max(w, rail.clientWidth - w);
      }

      // Sub-pixel layout routinely leaves a stray fraction of overflow, and
      // scrollLeft lands a hair short of the end, so everything gets slack.
      function update() {
        var max = rail.scrollWidth - rail.clientWidth;
        if (max <= 2) {
          nav.hidden = true;
          return;
        }
        nav.hidden = false;
        if (prev) prev.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= max - 2;
      }

      function go(dir) {
        rail.scrollBy({ left: dir * step(), behavior: 'smooth' });
      }

      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });
      rail.addEventListener('scroll', update, { passive: true });

      window.addEventListener('resize', debounce(update, 150), {
        signal: listeners.signal
      });

      // Thumbnails carry width/height so the rail measures correctly before
      // they load, but fonts landing late can still nudge card heights.
      window.addEventListener('load', update, { signal: listeners.signal });

      update();
    });
  }

  function initVideo() {
    initThumbFallbacks();
    initVideoRail();

    var triggers = $$('[data-hh-video]');
    if (!triggers.length) return;

    var dialog = $('[data-hh-lightbox]');
    if (!dialog || typeof dialog.showModal !== 'function') return; // graceful: links still work

    var frame = $('[data-hh-lightbox-frame]', dialog);
    var caption = $('[data-hh-lightbox-caption]', dialog);
    var closeBtn = $('[data-hh-lightbox-close]', dialog);
    var lastFocus = null;

    function open(id, title, ratio) {
      if (!id) return;
      lastFocus = document.activeElement;
      dialog.setAttribute('data-ratio', ratio || '9:16');
      if (caption) caption.textContent = title || '';
      var iframe = document.createElement('iframe');
      iframe.src =
        'https://www.youtube-nocookie.com/embed/' +
        encodeURIComponent(id) +
        '?autoplay=1&rel=0&playsinline=1&modestbranding=1';
      iframe.title = title || 'Little Stackers tutorial video';
      iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      frame.replaceChildren(iframe);
      dialog.showModal();
      // showModal() makes the page inert but doesn't stop it scrolling behind.
      document.documentElement.style.overflow = 'hidden';
    }

    dialog.addEventListener('close', function () {
      frame.replaceChildren(); // unmounting the iframe stops playback
      document.documentElement.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    });

    // Click on the backdrop (i.e. the dialog element itself) closes it.
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) dialog.close();
    });
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        dialog.close();
      });
    }

    triggers.forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        open(
          el.getAttribute('data-hh-video'),
          el.getAttribute('data-hh-video-title'),
          el.getAttribute('data-hh-video-ratio')
        );
      });
    });
  }

  /* ---------------------------------------------------------------------
     4. Contact form — validation, counter, topic deflection
     --------------------------------------------------------------------- */

  function initContact() {
    // `data-hh-form` sits on a div *inside* the Liquid-generated <form> (the
    // {% form %} tag owns the form element itself). Submit events don't reach
    // descendants, so the listener has to go on the real form.
    var root = $('[data-hh-form]');
    if (!root) return;
    var form = root.closest('form') || root;

    /* --- character counter --- */
    var textarea = $('[data-hh-counter-input]', form);
    var counter = $('[data-hh-counter]', form);
    if (textarea && counter) {
      var max = parseInt(textarea.getAttribute('maxlength'), 10) || 2000;
      var updateCount = function () {
        counter.textContent = textarea.value.length + ' / ' + max;
      };
      textarea.addEventListener('input', updateCount);
      updateCount();
    }

    /* --- topic-driven deflection --- */
    var topic = $('[data-hh-topic]', form);
    var panel = $('[data-hh-deflect]', form);
    // On /pages/contact there's no FAQ section to index, so suggestions link
    // across to the help hub instead of the panel silently never appearing.
    var faqUrl = (root.getAttribute('data-hh-faq-url') || '').trim();

    if (topic && panel && (index.length || faqUrl)) {
      var listEl = $('[data-hh-deflect-list]', panel);
      var noteEl = $('[data-hh-deflect-note]', panel);

      topic.addEventListener('change', function () {
        var opt = topic.options[topic.selectedIndex];
        var category = opt ? opt.getAttribute('data-faq-category') : '';
        var videoId = opt ? opt.getAttribute('data-video-id') : '';
        var videoTitle = opt ? opt.getAttribute('data-video-title') : '';

        if (!category && !videoId) {
          panel.classList.remove('is-shown');
          return;
        }

        var matches = index
          .filter(function (entry) {
            return entry.category === category;
          })
          .slice(0, 3);

        // No local index (contact page): offer the category on the FAQ page.
        var crossLink = !matches.length && category && faqUrl
          ? faqUrl.replace(/#.*$/, '') + '#' + category
          : '';

        if (!matches.length && !videoId && !crossLink) {
          panel.classList.remove('is-shown');
          return;
        }

        var html = matches
          .map(function (entry) {
            return (
              '<li><a href="#' + entry.id + '">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>' +
              escapeHtml(entry.question) +
              '</a></li>'
            );
          })
          .join('');

        if (crossLink) {
          html +=
            '<li><a href="' + escapeHtml(crossLink) + '">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>' +
            'Read our ' + escapeHtml((opt.textContent || '').trim().toLowerCase()) + ' answers' +
            '</a></li>';
        }

        if (videoId) {
          html +=
            '<li><a href="https://www.youtube.com/shorts/' + encodeURIComponent(videoId) + '"' +
            ' data-hh-video="' + escapeHtml(videoId) + '"' +
            ' data-hh-video-title="' + escapeHtml(videoTitle || '') + '">' +
            '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>' +
            'Watch: ' + escapeHtml(videoTitle || 'tutorial video') +
            '</a></li>';
        }

        listEl.innerHTML = html;
        if (noteEl) {
          noteEl.textContent = matches.length
            ? 'These usually sort it in under a minute. Still stuck? Finish the form below and we’ll get back to you.'
            : 'Have a quick look at this first — otherwise finish the form and we’ll get back to you.';
        }
        panel.classList.add('is-shown');

        // Newly-rendered video chips need the lightbox wired up.
        $$('[data-hh-video]', listEl).forEach(function (el) {
          if (el.__hhBound) return;
          el.__hhBound = true;
          el.addEventListener('click', function (e) {
            e.preventDefault();
            var trigger = $('[data-hh-video="' + el.getAttribute('data-hh-video') + '"][data-hh-video-card]');
            if (trigger) trigger.click();
            else window.open(el.href, '_blank', 'noopener');
          });
        });
      });
    }

    /* --- inline validation ---
       Shopify validates server-side too; this is purely so people don't lose
       a long message to a round-trip over a typo'd email. */
    var fields = $$('[data-hh-validate]', form);

    function validate(field) {
      var control = $('input, select, textarea', field);
      if (!control) return true;
      var wasInvalid = field.classList.contains('is-invalid');
      var ok = control.checkValidity() && !(control.required && !control.value.trim());
      field.classList.toggle('is-invalid', !ok);
      control.setAttribute('aria-invalid', ok ? 'false' : 'true');
      if (!ok && !wasInvalid) {
        var err = $('[data-hh-error]', field);
        if (err && !err.textContent.trim()) err.textContent = control.validationMessage;
      }
      return ok;
    }

    fields.forEach(function (field) {
      var control = $('input, select, textarea', field);
      if (!control) return;
      control.addEventListener('blur', function () {
        validate(field);
      });
      // Only re-check on input once it's already been flagged — no nagging.
      control.addEventListener('input', function () {
        if (field.classList.contains('is-invalid')) validate(field);
      });
    });

    var honeypot = $('[data-hh-honeypot]', form);

    form.addEventListener('submit', function (e) {
      // Blocks the subset of bots that execute JS. Ones that don't still get
      // caught downstream — a filled "Website" line is a spam tell in the inbox.
      if (honeypot && honeypot.value) {
        e.preventDefault();
        return;
      }

      var firstBad = null;
      fields.forEach(function (field) {
        if (!validate(field) && !firstBad) firstBad = field;
      });
      if (firstBad) {
        e.preventDefault();
        scrollToEl(firstBad);
        var control = $('input, select, textarea', firstBad);
        if (control) control.focus({ preventScroll: true });
      }
    });

    // Land on the form's success/error banner after a submit round-trip. A
    // contact form POSTs away to /contact and comes back as a fresh page load,
    // so without this you're returned to the top of the page with no sign that
    // anything happened.
    landOn($('[data-hh-alert]', form));
  }

  /* ---------------------------------------------------------------------
     5. Landing on a #section anchor that came in with the URL
     --------------------------------------------------------------------- */

  // Only for anchors already in the URL on first paint, which the browser
  // fumbles for the reason described on landOn. In-page clicks are left alone:
  // by then the page has loaded and the smooth scroll works properly.
  //
  // Two flows depend on this. Coming back from sign-in, where the wholesale
  // gate sends return_to=...%23wholesale-enquiry so the retailer lands on the
  // application form rather than the masthead; and any shared link to a
  // section anchor.
  function initHashLanding() {
    var hash = location.hash;
    if (!hash || hash === '#') return;

    var el;
    try {
      el = document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch (e) {
      return;
    }
    if (!el) return;

    // FAQ items have their own handler, which has to open the item first.
    if (el.hasAttribute('data-hh-faq-item')) return;

    landOn(el);
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */

  function init() {
    if (listeners) listeners.abort();
    listeners = new AbortController();

    buildIndex();
    initSearch();
    initFaq();
    initVideo();
    initContact();
    initHashLanding();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the theme editor re-renders one of our sections.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('.hh')) {
      faqRoot = $('[data-hh-faq]');
      init();
    }
  });
})();
